import { Fragment, type ReactElement, type ReactNode, useMemo } from "react";
import {
  CheckmarkIcon,
  CloseIcon,
  Loading01Icon,
  TimeOclockIcon,
} from "vega-icons";

import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import {
  ThemedButton,
  type ThemedButtonProps,
} from "@vega-core/components/Buttons/ThemedButton";
import { useIgnoreScrollbarClose } from "@vega-core/hooks/useIgnoreScrollbarClose";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";

const getIllustration = (
  illustration?: ReactElement,
  variant?: "error" | "loading" | "pending" | "success",
) => {
  if (illustration) return illustration;

  const variantConfig = {
    success: {
      borderColor: "border-text/positive/10",
      bgColor: "bg-success/5",
      icon: (
        <CheckmarkIcon
          className="w-12 h-12 sm:w-16 sm:h-16 text-text/positive"
          data-testid="CheckmarkIcon"
        />
      ),
    },
    pending: {
      borderColor: "border-text/pending/10",
      bgColor: "bg-warning/5",
      icon: (
        <TimeOclockIcon
          className="w-12 h-12 sm:w-16 sm:h-16 text-text/pending"
          data-testid="TimeOclockIcon"
        />
      ),
    },
    error: {
      borderColor: "border-text/negative/10",
      bgColor: "bg-error/5",
      icon: (
        <CloseIcon
          className="w-12 h-12 sm:w-16 sm:h-16 text-text/negative"
          data-testid="CloseIcon"
        />
      ),
    },
    loading: {
      borderColor: "border-text/interactive/10",
      bgColor: "bg-primary/5",
      icon: (
        <Loading01Icon
          className="w-12 h-12 sm:w-16 sm:h-16 text-text/interactive animate-spin"
          data-testid="Loading01Icon"
        />
      ),
    },
  };

  if (variant && variant in variantConfig) {
    const config = variantConfig[variant];

    return (
      <div
        className={`flex items-center justify-center w-32 h-32 sm:w-40 sm:h-40 rounded-full border-[1rem] sm:border-[1.25rem] ${config.borderColor} ${config.bgColor} p-4`}
      >
        {config.icon}
      </div>
    );
  }

  return null;
};

type Props = {
  title: string;
  description?: ReactNode;
  illustration?: ReactElement;
  primaryBtnProps?: Omit<ThemedButtonProps, "dataTestId">;
  secondaryBtnProps?: Omit<ThemedButtonProps, "dataTestId">;
  isOpen?: boolean;
  onClose?: VoidFunction;
  size?: "large" | "small";
  variant?: "error" | "loading" | "pending" | "success";
};

export function InformativeModal({
  title,
  isOpen,
  primaryBtnProps,
  secondaryBtnProps,
  illustration,
  onClose,
  size = "large",
  variant,
  description,
}: Props) {
  const onModalClose = () => onClose && onClose();
  const handleClose = useIgnoreScrollbarClose({
    open: isOpen ?? false,
    onClose: onModalClose,
  });

  const modalIllustration = useMemo(
    () => getIllustration(illustration, variant),
    [illustration, variant],
  );

  return (
    <Transition as={Fragment} show={isOpen}>
      <Dialog
        as="div"
        className="relative z-modal text-text/primary"
        data-testid="informative-modal-dialog"
        onClose={handleClose}
      >
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 overlay transition-opacity" />
        </TransitionChild>

        <div className="fixed inset-0 z-modal overflow-y-auto">
          <div className="flex items-center justify-center min-h-full text-center">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <DialogPanel
                className="relative overflow-hidden transform w-full max-w-[95vw] sm:max-w-sm p-4 sm:p-8 bg-background/card rounded-containerCorner border-border/secondary shadow-popover"
                data-testid="informative-modal-panel"
              >
                <div className="flex justify-center mb-4 sm:mb-6">
                  {modalIllustration}
                </div>
                <DialogTitle className="text-center w-full flex flex-col mb-6 sm:mb-8 gap-2">
                  <span
                    className={cnTwMerge(
                      "heading-5 sm:heading-4 text-text/primary",
                      {
                        "heading-4": size === "large",
                        "heading-5": size === "small",
                      },
                    )}
                  >
                    {title}
                  </span>
                  {description ? (
                    <span className="body-sm-regular sm:body-base-regular text-text/secondary">
                      {description}
                    </span>
                  ) : null}
                </DialogTitle>
                <div className="flex w-full flex-col justify-end gap-2">
                  {primaryBtnProps ? (
                    <ThemedButton
                      fullWidth
                      variant="primary"
                      {...primaryBtnProps}
                      dataTestId="informative-modal-primary-button"
                    />
                  ) : null}
                  {secondaryBtnProps ? (
                    <ThemedButton
                      fullWidth
                      variant="secondary"
                      {...secondaryBtnProps}
                      dataTestId="informative-modal-secondary-button"
                    />
                  ) : null}
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
